// Pure 2D side-scrolling platformer movement, jumping, abilities, and collision against
// a tilemap grid. The renderer and game shells consume these; they carry no
// three.js, Phaser, or DOM dependencies so physics and abilities remain fully
// deterministic and unit-testable.
import type { Sprite2D, TileCollisionKind, Tilemap2D, Tilemap2DTriggerVolume } from '../../content/schema/map';
import type { AttackState, EnemyDef, EnemyRuntime, PickupDef, Projectile, WeaponSpec } from './combat2d';
import { boxesOverlap, createEnemyRuntime, meleeHitbox } from './combat2d';
import { damageEnemy, makeProjectile, stepEnemies } from './enemies2d';
import type { Tilemap2DTriggerVolume as TriggerVolume } from '../../content/schema/map';

/** World units per tile. */
export const TILE_SIZE = 1;

/** The player character's collision box half-extents in tiles. */
export const PLAYER_HALF_WIDTH = 0.3;
export const PLAYER_HALF_HEIGHT = 0.4;

export interface PlatformerTile {
  kind: TileCollisionKind;
}

export interface PlatformerGrid {
  width: number;
  height: number;
  tiles: (TileCollisionKind | null)[];
}

export interface PlatformerAbilities {
  doubleJump?: boolean;
  wallJump?: boolean;
  dash?: boolean;
  teleport?: boolean;
  shadowForm?: boolean;
  rewind?: boolean;
  ghostDouble?: boolean;
  moonlit?: boolean;
  ledgeGrab?: boolean;
}

export interface PlatformerConfig {
  gravity: number;
  maxFallSpeed: number;
  glideFallSpeed: number;
  runSpeed: number;
  acceleration: number;
  deceleration: number;
  jumpImpulse: number;
  jumpHoldGraceSeconds: number;
  coyoteTimeSeconds: number;
  jumpBufferSeconds: number;
  wallSlideSpeed: number;
  wallJumpImpulseX: number;
  wallJumpImpulseY: number;
  dashSpeed: number;
  dashDurationSeconds: number;
  dashCooldownSeconds: number;
  teleportDistance: number;
  teleportCooldownSeconds: number;
  shadowFormDurationSeconds: number;
  shadowFormCooldownSeconds: number;
  conveyorSpeed: number;
  /** Seconds a dead player stays in the death pose before respawning at
   * their last checkpoint (or the map's spawn, if none was reached yet). */
  respawnDelaySeconds: number;
  /** Hits the player can take from enemies before dying. Hazards always
   * kill outright regardless of health. */
  maxHealth: number;
  /** Seconds of invulnerability after taking damage. */
  invulnerabilitySeconds: number;
  /** Seconds the player's controls are locked after taking damage. */
  hurtStunSeconds: number;
  /** Knockback impulse applied when hurt, tiles per second. */
  hurtKnockbackX: number;
  hurtKnockbackY: number;
  /** Movement is sub-stepped so no single sub-step moves the player more
   * than this many tiles; keeps a dash from tunnelling through a 1-tile
   * wall on a slow frame. */
  maxSubstepDistance: number;
}

export const DEFAULT_PLATFORMER_CONFIG: PlatformerConfig = {
  gravity: 30.0,
  maxFallSpeed: 18.0,
  glideFallSpeed: 3.5,
  runSpeed: 8.0,
  acceleration: 60.0,
  deceleration: 50.0,
  jumpImpulse: 11.5,
  jumpHoldGraceSeconds: 0.18,
  coyoteTimeSeconds: 0.12,
  jumpBufferSeconds: 0.10,
  wallSlideSpeed: 3.0,
  wallJumpImpulseX: 9.0,
  wallJumpImpulseY: 10.5,
  dashSpeed: 22.0,
  dashDurationSeconds: 0.16,
  dashCooldownSeconds: 0.35,
  teleportDistance: 4.5,
  teleportCooldownSeconds: 0.8,
  shadowFormDurationSeconds: 1.5,
  shadowFormCooldownSeconds: 2.5,
  conveyorSpeed: 4.0,
  respawnDelaySeconds: 1.0,
  maxHealth: 3,
  invulnerabilitySeconds: 1.0,
  hurtStunSeconds: 0.22,
  hurtKnockbackX: 6,
  hurtKnockbackY: 6,
  maxSubstepDistance: 0.25,
};

export type VerticalState =
  | 'grounded'
  | 'jumping'
  | 'falling'
  | 'wall-sliding'
  | 'dashing'
  | 'teleporting'
  | 'phasing'
  | 'rewinding'
  | 'gliding';

export type Facing = 'left' | 'right';

export interface PlatformerInput {
  moveX: number; // -1 to 1
  moveY?: number; // -1 (up) to 1 (down)
  jumpPressed?: boolean;
  jumpHeld?: boolean;
  dashPressed?: boolean;
  teleportPressed?: boolean;
  shadowFormPressed?: boolean;
  rewindHeld?: boolean;
  ghostDoublePressed?: boolean;
  /** Edge-triggered: the attack button went down this step. */
  attackPressed?: boolean;
  /** Level-triggered: the attack button is currently held (charge weapons). */
  attackHeld?: boolean;
}

export interface HistoryFrame {
  x: number;
  y: number;
  vx: number;
  vy: number;
  facing: Facing;
}

export interface GhostDoubleState {
  active: boolean;
  x: number;
  y: number;
  facing: Facing;
}

export interface CrumbleTileState {
  index: number;
  timer: number; // > 0 crumbling/falling, < 0 respawn countdown
  fallen: boolean;
}

/**
 * Dynamic entity kinds.
 * - `moving-platform`: carries the player. Moves between `minX`/`maxX` or
 *   `minY`/`maxY` on a sine with `period` seconds (and `phase` in 0..1),
 *   or ping-pongs at `vx`/`vy` tiles per second when no `period` is given.
 * - `moving-hazard`: same motion model; lethal on contact (a pendulum, a
 *   press). `width`/`height` give its box.
 * - `steam-vent`: an updraft column of `height` tiles above `y`.
 * - `patrol-bird`: legacy instant-kill contact hazard, kept for old maps.
 * - `enemy`: see `EnemyDef`; the live state lives in `PlatformerState.enemies`.
 * - `pickup`: see `PickupDef`; collected once per attempt.
 */
export type DynamicEntityType =
  | 'moving-platform'
  | 'moving-hazard'
  | 'patrol-bird'
  | 'steam-vent'
  | 'enemy'
  | 'pickup';

export interface DynamicEntity {
  id: string;
  type: DynamicEntityType;
  x: number;
  y: number;
  vx?: number;
  vy?: number;
  width?: number;
  height?: number;
  minX?: number;
  maxX?: number;
  minY?: number;
  maxY?: number;
  period?: number;
  phase?: number;
  /**
   * Optional baked pixel art for this entity, the `DynamicEntity` analogue
   * of `Tilemap2DTile.art`. When present, a renderer should paint this
   * instead of its built-in flat-colour placeholder mesh. Absent (the
   * default) keeps every entity authored before this field existed
   * rendering exactly as it did.
   */
  art?: Sprite2D;
  /** Required when `type` is 'enemy'. */
  enemy?: EnemyDef;
  /** Required when `type` is 'pickup'. */
  pickup?: PickupDef;
  /** Initial facing for enemies. Defaults to 'left'. */
  facing?: 'left' | 'right';
}

/**
 * Where a moving entity is at `elapsed` seconds into the attempt. Pure, so
 * the physics step and the renderer agree exactly. Static entities return
 * their authored position.
 */
export function dynamicEntityPosition(ent: DynamicEntity, elapsed: number): { x: number; y: number } {
  if (ent.type !== 'moving-platform' && ent.type !== 'moving-hazard') return { x: ent.x, y: ent.y };
  let x = ent.x;
  let y = ent.y;
  const hasX = ent.minX !== undefined && ent.maxX !== undefined && ent.maxX > ent.minX;
  const hasY = ent.minY !== undefined && ent.maxY !== undefined && ent.maxY > ent.minY;
  if (ent.period && ent.period > 0) {
    const t = elapsed / ent.period + (ent.phase ?? 0);
    const s = (Math.sin(t * Math.PI * 2 - Math.PI / 2) + 1) / 2; // 0..1, starts at min
    if (hasX) x = ent.minX! + (ent.maxX! - ent.minX!) * s;
    if (hasY) y = ent.minY! + (ent.maxY! - ent.minY!) * s;
    return { x, y };
  }
  if (hasX && ent.vx) {
    const span = ent.maxX! - ent.minX!;
    const d = (Math.abs(ent.vx) * elapsed + (ent.x - ent.minX!)) % (span * 2);
    x = ent.minX! + (d <= span ? d : span * 2 - d);
  }
  if (hasY && ent.vy) {
    const span = ent.maxY! - ent.minY!;
    const d = (Math.abs(ent.vy) * elapsed + (ent.y - ent.minY!)) % (span * 2);
    y = ent.minY! + (d <= span ? d : span * 2 - d);
  }
  return { x, y };
}

export interface PlatformerState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  verticalState: VerticalState;
  facing: Facing;
  isGrounded: boolean;
  wallCling: 'left' | 'right' | null;
  coyoteTimer: number;
  jumpBufferTimer: number;
  jumpHoldTimer: number;
  dashTimer: number;
  dashCooldownTimer: number;
  teleportCooldownTimer: number;
  shadowFormTimer: number;
  shadowFormCooldownTimer: number;
  doubleJumpAvailable: boolean;
  isDead: boolean;
  deathReason: string | null;
  /** Seconds elapsed since `isDead` became true; drives the respawn delay.
   * 0 while alive. */
  deathTimer: number;
  checkpoint: { x: number; y: number } | null;
  history: HistoryFrame[];
  ghostDouble: GhostDoubleState | null;
  crumbleTiles: Record<number, CrumbleTileState>;
  /** Seconds since this attempt (spawn or respawn) began. Drives moving
   * entity positions via `dynamicEntityPosition`. */
  elapsed: number;
  /** Hits remaining before the next enemy hit kills. */
  health: number;
  /** Seconds of post-hit invulnerability remaining. */
  invulnTimer: number;
  /** Seconds of post-hit control lock remaining. */
  hurtTimer: number;
  attack: AttackState | null;
  attackCooldownTimer: number;
  /** Seconds the attack button has been held (charge weapons). */
  chargeTimer: number;
  /** Live enemies keyed by their `DynamicEntity.id`. */
  enemies: Record<string, EnemyRuntime>;
  projectiles: Projectile[];
  /** Ids of pickups already collected this attempt. */
  pickupsCollected: string[];
  /** Ids of trigger volumes the player is currently inside. */
  activeTriggers: string[];
  /** Set once the goal tile is touched; the step stops emitting after. */
  hasWon: boolean;
}

export type PlatformerEvent =
  | { type: 'jump' }
  | { type: 'double-jump' }
  | { type: 'wall-jump'; direction: 'left' | 'right' }
  | { type: 'dash'; direction: Facing }
  | { type: 'teleport'; from: { x: number; y: number }; to: { x: number; y: number } }
  | { type: 'shadow-form'; active: boolean }
  | { type: 'rewind'; active: boolean }
  | { type: 'ghost-double'; position: { x: number; y: number } }
  | { type: 'glide'; active: boolean }
  | { type: 'land' }
  | { type: 'wall-slide' }
  | { type: 'death'; reason: string; x: number; y: number }
  | { type: 'checkpoint'; x: number; y: number }
  | { type: 'respawn'; x: number; y: number }
  | { type: 'attack'; weaponId: string; x: number; y: number; facing: Facing }
  | { type: 'hit'; enemyId: string; damage: number; x: number; y: number; weaponId: string }
  | { type: 'enemy-death'; enemyId: string; x: number; y: number; tag?: string }
  | { type: 'enemy-telegraph'; enemyId: string; x: number; y: number }
  | { type: 'enemy-attack'; enemyId: string; x: number; y: number }
  | { type: 'hurt'; damage: number; health: number; x: number; y: number; source: string }
  | { type: 'pickup'; pickupId: string; kind: PickupDef['kind']; itemId: string; x: number; y: number }
  | { type: 'projectile'; owner: 'player' | 'enemy'; kind?: string; x: number; y: number }
  | { type: 'trigger-enter'; triggerId: string; eventName?: string; params?: Record<string, unknown> }
  | { type: 'trigger-exit'; triggerId: string; eventName?: string }
  | { type: 'victory'; x: number; y: number };

/** Optional per-step context beyond the classic positional arguments. */
export interface PlatformerStepExtras {
  /** The equipped weapon; null or undefined means the player cannot attack. */
  weapon?: WeaponSpec | null;
  /** Trigger volumes to diff the player against each step. */
  triggers?: Tilemap2DTriggerVolume[];
}

/** Build the collision grid from a Tilemap2D. */
export function buildPlatformerGrid(map: Tilemap2D): PlatformerGrid {
  const byId = new Map(
    map.tiles.map((t) => {
      let kind: TileCollisionKind = 'none';
      if (t.kind) {
        kind = t.kind;
      } else if (t.solid) {
        kind = 'solid';
      }
      return [t.id, kind];
    }),
  );

  const tiles = map.cells.map((cell) => {
    if (cell === null) return 'none'; // empty air
    return byId.get(cell) ?? 'none';
  });

  return {
    width: map.width,
    height: map.height,
    tiles,
  };
}

export function getTileKindAt(
  grid: PlatformerGrid,
  tileX: number,
  tileY: number,
  crumbleStates?: Record<number, CrumbleTileState>,
): TileCollisionKind {
  const cx = Math.floor(tileX);
  const cy = Math.floor(tileY);
  if (cx < 0 || cx >= grid.width || cy < 0 || cy >= grid.height) {
    return 'solid';
  }
  const idx = cy * grid.width + cx;
  if (crumbleStates && crumbleStates[idx]?.fallen) {
    return 'none';
  }
  return grid.tiles[idx] ?? 'solid';
}

export function isSolidAt(
  grid: PlatformerGrid,
  tileX: number,
  tileY: number,
  isPhasing = false,
  crumbleStates?: Record<number, CrumbleTileState>,
): boolean {
  const kind = getTileKindAt(grid, tileX, tileY, crumbleStates);
  if (isPhasing && kind === 'phase-barrier') {
    return false;
  }
  return (
    kind === 'solid' ||
    kind === 'crumble' ||
    kind === 'phase-barrier' ||
    kind === 'goal' ||
    kind === 'conveyor-left' ||
    kind === 'conveyor-right'
  );
}

export function isGoalAt(grid: PlatformerGrid, tileX: number, tileY: number): boolean {
  return getTileKindAt(grid, tileX, tileY) === 'goal';
}

/** True when an AABB (slightly grown, so touching the bell counts) meets a goal tile. */
export function boxIntersectsGoal(grid: PlatformerGrid, cx: number, cy: number, hw = PLAYER_HALF_WIDTH + 0.1, hh = PLAYER_HALF_HEIGHT + 0.1): boolean {
  const minX = Math.floor(cx - hw);
  const maxX = Math.floor(cx + hw);
  const minY = Math.floor(cy - hh);
  const maxY = Math.floor(cy + hh);
  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      if (isGoalAt(grid, x, y)) return true;
    }
  }
  return false;
}

export function isHazardAt(grid: PlatformerGrid, tileX: number, tileY: number, isPhasing = false): boolean {
  if (isPhasing) return false;
  return getTileKindAt(grid, tileX, tileY) === 'hazard';
}

export function isCheckpointAt(grid: PlatformerGrid, tileX: number, tileY: number): boolean {
  return getTileKindAt(grid, tileX, tileY) === 'checkpoint';
}

export function isOneWayAt(grid: PlatformerGrid, tileX: number, tileY: number): boolean {
  return getTileKindAt(grid, tileX, tileY) === 'one-way';
}

export function getConveyorDirection(grid: PlatformerGrid, tileX: number, tileY: number): 'left' | 'right' | null {
  const kind = getTileKindAt(grid, tileX, tileY);
  if (kind === 'conveyor-left') return 'left';
  if (kind === 'conveyor-right') return 'right';
  return null;
}

/** Check if an AABB collides with solid tiles. */
export function boxCollidesSolid(
  grid: PlatformerGrid,
  cx: number,
  cy: number,
  hw = PLAYER_HALF_WIDTH,
  hh = PLAYER_HALF_HEIGHT,
  isPhasing = false,
  crumbleStates?: Record<number, CrumbleTileState>,
): boolean {
  const minX = Math.floor(cx - hw);
  const maxX = Math.floor(cx + hw);
  const minY = Math.floor(cy - hh);
  const maxY = Math.floor(cy + hh);

  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      if (isSolidAt(grid, x, y, isPhasing, crumbleStates)) {
        return true;
      }
    }
  }
  return false;
}

/** Check if an AABB intersects any hazard tiles. */
export function boxIntersectsHazard(
  grid: PlatformerGrid,
  cx: number,
  cy: number,
  hw = PLAYER_HALF_WIDTH * 0.8,
  hh = PLAYER_HALF_HEIGHT * 0.8,
  isPhasing = false,
): boolean {
  if (isPhasing) return false;
  const minX = Math.floor(cx - hw);
  const maxX = Math.floor(cx + hw);
  const minY = Math.floor(cy - hh);
  const maxY = Math.floor(cy + hh);

  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      if (isHazardAt(grid, x, y, isPhasing)) {
        return true;
      }
    }
  }
  return false;
}

/** Check if an AABB intersects any checkpoint tiles. */
export function boxIntersectsCheckpoint(
  grid: PlatformerGrid,
  cx: number,
  cy: number,
  hw = PLAYER_HALF_WIDTH,
  hh = PLAYER_HALF_HEIGHT,
): { x: number; y: number } | null {
  const minX = Math.floor(cx - hw);
  const maxX = Math.floor(cx + hw);
  const minY = Math.floor(cy - hh);
  const maxY = Math.floor(cy + hh);

  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      if (isCheckpointAt(grid, x, y)) {
        return { x: x + 0.5, y: y + 0.5 };
      }
    }
  }
  return null;
}

/** Initialise player state at a spawn position or checkpoint. Enemies and
 * pickups reset with the attempt (a respawn resets the room, Meat Boy
 * style); collected pickups a game wants to keep must be persisted by the
 * game from the `pickup` event. */
export function createInitialPlatformerState(
  spawn: { x: number; y: number },
  config: PlatformerConfig = DEFAULT_PLATFORMER_CONFIG,
  dynamicEntities: DynamicEntity[] = [],
): PlatformerState {
  const enemies: Record<string, EnemyRuntime> = {};
  for (const ent of dynamicEntities) {
    if (ent.type === 'enemy' && ent.enemy) {
      enemies[ent.id] = createEnemyRuntime(ent.id, ent.enemy, { x: ent.x, y: ent.y }, ent.facing ?? 'left');
    }
  }
  return {
    x: spawn.x,
    y: spawn.y,
    vx: 0,
    vy: 0,
    verticalState: 'falling',
    facing: 'right',
    isGrounded: false,
    wallCling: null,
    coyoteTimer: 0,
    jumpBufferTimer: 0,
    jumpHoldTimer: 0,
    dashTimer: 0,
    dashCooldownTimer: 0,
    teleportCooldownTimer: 0,
    shadowFormTimer: 0,
    shadowFormCooldownTimer: 0,
    doubleJumpAvailable: false,
    isDead: false,
    deathReason: null,
    deathTimer: 0,
    checkpoint: { x: spawn.x, y: spawn.y },
    history: [],
    ghostDouble: null,
    crumbleTiles: {},
    elapsed: 0,
    health: config.maxHealth,
    invulnTimer: 0,
    hurtTimer: 0,
    attack: null,
    attackCooldownTimer: 0,
    chargeTimer: 0,
    enemies,
    projectiles: [],
    pickupsCollected: [],
    activeTriggers: [],
    hasWon: false,
  };
}

/**
 * Step the platformer simulation by `dt` seconds.
 * Pure deterministic transition returning updated state and emitted events.
 *
 * Once-per-step work (timers, ability triggers, input edges, enemy AI,
 * pickups, triggers, the goal) runs with the full `dt`; movement and every
 * collision test run in sub-steps small enough that nothing moves further
 * than `config.maxSubstepDistance` at a time, so a dash on a slow frame
 * still stops at a one-tile wall.
 */
export function stepPlatformer(
  state: PlatformerState,
  input: PlatformerInput,
  grid: PlatformerGrid,
  dt: number,
  abilities: PlatformerAbilities = {},
  config: PlatformerConfig = DEFAULT_PLATFORMER_CONFIG,
  dynamicEntities: DynamicEntity[] = [],
  extras: PlatformerStepExtras = {},
): { state: PlatformerState; events: PlatformerEvent[] } {
  if (state.isDead) {
    // Hold the death pose for `respawnDelaySeconds`, then respawn at the
    // last checkpoint (or the map's own spawn, if none was reached yet).
    const deathTimer = state.deathTimer + dt;
    if (deathTimer < config.respawnDelaySeconds) {
      return { state: { ...state, deathTimer }, events: [] };
    }
    const respawnAt = state.checkpoint ?? { x: state.x, y: state.y };
    return {
      state: createInitialPlatformerState(respawnAt, config, dynamicEntities),
      events: [{ type: 'respawn', x: respawnAt.x, y: respawnAt.y }],
    };
  }

  const events: PlatformerEvent[] = [];
  let {
    x,
    y,
    vx,
    vy,
    verticalState,
    facing,
    isGrounded,
    wallCling,
    coyoteTimer,
    jumpBufferTimer,
    jumpHoldTimer,
    dashTimer,
    dashCooldownTimer,
    teleportCooldownTimer,
    shadowFormTimer,
    shadowFormCooldownTimer,
    doubleJumpAvailable,
    checkpoint,
    ghostDouble,
    crumbleTiles,
    elapsed,
    health,
    invulnTimer,
    hurtTimer,
    attack,
    attackCooldownTimer,
    chargeTimer,
    enemies,
    projectiles,
    pickupsCollected,
    activeTriggers,
    hasWon,
  } = state;
  const history = state.history.slice();

  // After the bell rings the player keeps physics (so they land) but no
  // longer drives it; the shell decides when to leave the level.
  if (hasWon || hurtTimer > 0) {
    input = {
      moveX: 0,
      jumpHeld: hurtTimer > 0 ? false : input.jumpHeld,
    };
  }

  // 1. Ability: Time Rewind (Life 7)
  if (abilities.rewind && input.rewindHeld && history.length > 0) {
    const rewindSpeed = 2;
    let targetFrame: HistoryFrame | null = null;
    for (let r = 0; r < rewindSpeed && history.length > 0; r++) {
      targetFrame = history.pop() ?? null;
    }
    if (targetFrame) {
      events.push({ type: 'rewind', active: true });
      return {
        state: {
          ...state,
          x: targetFrame.x,
          y: targetFrame.y,
          vx: targetFrame.vx * 0.5,
          vy: targetFrame.vy * 0.5,
          facing: targetFrame.facing,
          verticalState: 'rewinding',
          history,
          elapsed: elapsed + dt,
          invulnTimer: Math.max(invulnTimer, 0.2),
          hurtTimer: 0,
          attack: null,
        },
        events,
      };
    }
  }

  // History buffer (max 180 entries, about 3 s at 60 Hz).
  history.push({ x, y, vx, vy, facing });
  if (history.length > 180) history.shift();

  // 2. Timers decrement
  coyoteTimer = Math.max(0, coyoteTimer - dt);
  jumpBufferTimer = Math.max(0, jumpBufferTimer - dt);
  jumpHoldTimer = Math.max(0, jumpHoldTimer - dt);
  dashCooldownTimer = Math.max(0, dashCooldownTimer - dt);
  teleportCooldownTimer = Math.max(0, teleportCooldownTimer - dt);
  shadowFormCooldownTimer = Math.max(0, shadowFormCooldownTimer - dt);
  invulnTimer = Math.max(0, invulnTimer - dt);
  hurtTimer = Math.max(0, hurtTimer - dt);
  attackCooldownTimer = Math.max(0, attackCooldownTimer - dt);
  if (shadowFormTimer > 0) {
    shadowFormTimer = Math.max(0, shadowFormTimer - dt);
    if (shadowFormTimer === 0) {
      events.push({ type: 'shadow-form', active: false });
    }
  }

  // 3. Ability: Shadow Form Trigger (Life 6)
  if (abilities.shadowForm && input.shadowFormPressed && shadowFormCooldownTimer <= 0) {
    shadowFormTimer = config.shadowFormDurationSeconds;
    shadowFormCooldownTimer = config.shadowFormCooldownSeconds;
    verticalState = 'phasing';
    events.push({ type: 'shadow-form', active: true });
  }
  // Read after the trigger so intangibility applies from the frame Shadow
  // Form activates.
  const isPhasing = shadowFormTimer > 0;

  // 4. Ability: Ghost Double Trigger (Life 8)
  if (abilities.ghostDouble && input.ghostDoublePressed) {
    if (ghostDouble?.active) {
      ghostDouble = null;
    } else {
      ghostDouble = { active: true, x, y, facing };
      events.push({ type: 'ghost-double', position: { x, y } });
    }
  }

  // 5. Ability: Teleport Blink Trigger (Life 5)
  if (abilities.teleport && input.teleportPressed && teleportCooldownTimer <= 0) {
    const dist = config.teleportDistance;
    const dir = facing === 'right' ? 1 : -1;
    let targetX = x + dir * dist;
    for (let step = 0.5; step <= dist; step += 0.5) {
      const probeX = x + dir * step;
      if (boxCollidesSolid(grid, probeX, y, PLAYER_HALF_WIDTH, PLAYER_HALF_HEIGHT, isPhasing, crumbleTiles)) {
        targetX = probeX - dir * 0.6;
        break;
      }
    }
    events.push({ type: 'teleport', from: { x, y }, to: { x: targetX, y } });
    x = targetX;
    teleportCooldownTimer = config.teleportCooldownSeconds;
    verticalState = 'teleporting';
  }

  if (input.jumpPressed) {
    jumpBufferTimer = config.jumpBufferSeconds;
  }

  // Update facing
  if (input.moveX > 0.1) facing = 'right';
  else if (input.moveX < -0.1) facing = 'left';

  // 6. Dash trigger (Life 4). The dash itself moves in the sub-steps.
  if (dashTimer <= 0 && abilities.dash && input.dashPressed && dashCooldownTimer <= 0) {
    dashTimer = config.dashDurationSeconds;
    dashCooldownTimer = config.dashCooldownSeconds;
    verticalState = 'dashing';
    vy = 0;
    vx = facing === 'right' ? config.dashSpeed : -config.dashSpeed;
    events.push({ type: 'dash', direction: facing });
  }

  // 7. Attacks
  const weapon = extras.weapon ?? null;
  let lungeRemaining = 0;
  if (attack) {
    attack = { ...attack, timer: attack.timer - dt };
    if (attack.timer <= 0) {
      attackCooldownTimer = weapon && weapon.id === attack.weaponId ? weapon.cooldownSeconds : 0.2;
      attack = null;
    }
  }
  if (weapon && !attack && attackCooldownTimer <= 0 && hurtTimer <= 0 && !hasWon) {
    if (weapon.kind === 'charge') {
      if (input.attackHeld) {
        chargeTimer += dt;
      } else if (chargeTimer > 0) {
        const charged = chargeTimer >= (weapon.chargeSeconds ?? 0.4);
        chargeTimer = 0;
        attack = {
          weaponId: weapon.id,
          timer: weapon.activeSeconds,
          duration: weapon.activeSeconds,
          facing,
          hitIds: [],
          kind: charged ? 'charge' : 'melee',
        };
        if (charged) invulnTimer = Math.max(invulnTimer, weapon.activeSeconds);
        events.push({ type: 'attack', weaponId: weapon.id, x, y, facing });
      }
    } else if (input.attackPressed) {
      attack = {
        weaponId: weapon.id,
        timer: weapon.activeSeconds,
        duration: weapon.activeSeconds,
        facing,
        hitIds: [],
        kind: weapon.kind,
      };
      events.push({ type: 'attack', weaponId: weapon.id, x, y, facing });
      if (weapon.kind === 'ranged' && weapon.projectile) {
        const dir = facing === 'right' ? 1 : -1;
        projectiles = projectiles.concat(makeProjectile('player', weapon.projectile, x + dir * 0.4, y, dir, 0));
        events.push({ type: 'projectile', owner: 'player', kind: weapon.projectile.kind, x, y });
      }
    }
  } else if (!input.attackHeld) {
    chargeTimer = 0;
  }
  if (attack && attack.kind === 'charge' && weapon?.lungeDistance) {
    lungeRemaining = (weapon.lungeDistance / attack.duration) * dt;
  }

  // 8. Enemies (once per step, full dt). An enemy entity with no runtime
  // yet (a state created before the entity list was known) is seeded now.
  const defs: Record<string, EnemyDef> = {};
  for (const ent of dynamicEntities) {
    if (ent.type !== 'enemy' || !ent.enemy) continue;
    defs[ent.id] = ent.enemy;
    if (!enemies[ent.id]) {
      enemies = { ...enemies, [ent.id]: createEnemyRuntime(ent.id, ent.enemy, { x: ent.x, y: ent.y }, ent.facing ?? 'left') };
    }
  }
  let contactDamage = 0;
  let contactFromX = x;
  if (Object.keys(enemies).length > 0) {
    const enemyStep = stepEnemies(
      enemies,
      defs,
      grid,
      { x, y, hw: PLAYER_HALF_WIDTH * 0.8, hh: PLAYER_HALF_HEIGHT * 0.8, isPhasing },
      dt,
      crumbleTiles,
    );
    enemies = enemyStep.enemies;
    events.push(...enemyStep.events);
    if (enemyStep.projectiles.length) projectiles = projectiles.concat(enemyStep.projectiles);
    contactDamage = enemyStep.contactDamage;
    if (enemyStep.contactSources[0]) contactFromX = enemyStep.contactSources[0].x;
  }

  // 9. Sub-stepped movement, collision, projectiles and hitboxes.
  let maxSpeed = Math.max(config.dashSpeed, config.maxFallSpeed, config.runSpeed, Math.abs(vx), Math.abs(vy));
  for (const p of projectiles) maxSpeed = Math.max(maxSpeed, Math.hypot(p.vx, p.vy));
  const substeps = Math.max(1, Math.min(16, Math.ceil((maxSpeed * dt) / config.maxSubstepDistance)));
  const sdt = dt / substeps;
  let died: string | null = null;
  let landedThisStep = false;

  for (let s = 0; s < substeps && !died; s++) {
    const prevElapsed = elapsed;
    elapsed += sdt;

    if (dashTimer > 0) {
      dashTimer = Math.max(0, dashTimer - sdt);
      verticalState = 'dashing';
      vy = 0;
      vx = facing === 'right' ? config.dashSpeed : -config.dashSpeed;
      const nextX = x + vx * sdt;
      if (!boxCollidesSolid(grid, nextX, y, PLAYER_HALF_WIDTH, PLAYER_HALF_HEIGHT, isPhasing, crumbleTiles)) {
        x = nextX;
      } else {
        dashTimer = 0;
        vx = 0;
      }
    } else {
      // Horizontal acceleration and deceleration
      const controlled = hurtTimer <= 0;
      const targetVx = input.moveX * (abilities.moonlit ? config.runSpeed * 1.15 : config.runSpeed);
      if (controlled && Math.abs(input.moveX) > 0.1) {
        if (vx < targetVx) vx = Math.min(targetVx, vx + config.acceleration * sdt);
        else if (vx > targetVx) vx = Math.max(targetVx, vx - config.acceleration * sdt);
      } else {
        const decel = controlled ? config.deceleration : config.deceleration * 0.35;
        if (vx > 0) vx = Math.max(0, vx - decel * sdt);
        else if (vx < 0) vx = Math.min(0, vx + decel * sdt);
      }
      // Charge weapon lunge carries the player forward with collision.
      if (lungeRemaining > 0) {
        const dir = attack?.facing === 'right' ? 1 : -1;
        const step = lungeRemaining / (substeps - s);
        const lx = x + dir * step;
        if (!boxCollidesSolid(grid, lx, y, PLAYER_HALF_WIDTH, PLAYER_HALF_HEIGHT, isPhasing, crumbleTiles)) x = lx;
        lungeRemaining -= step;
        vy = Math.min(vy, 0);
      }

      // Wall contact before applying full gravity
      const touchingLeftWall =
        abilities.wallJump &&
        input.moveX < -0.1 &&
        boxCollidesSolid(grid, x - 0.05, y, PLAYER_HALF_WIDTH, PLAYER_HALF_HEIGHT, isPhasing, crumbleTiles);
      const touchingRightWall =
        abilities.wallJump &&
        input.moveX > 0.1 &&
        boxCollidesSolid(grid, x + 0.05, y, PLAYER_HALF_WIDTH, PLAYER_HALF_HEIGHT, isPhasing, crumbleTiles);

      if (!isGrounded && (touchingLeftWall || touchingRightWall) && vy > 0) {
        wallCling = touchingLeftWall ? 'left' : 'right';
        verticalState = 'wall-sliding';
        vy = Math.min(vy, config.wallSlideSpeed);
        if (s === 0) events.push({ type: 'wall-slide' });
      } else {
        wallCling = null;
      }

      // Jump / wall jump / double jump
      if (jumpBufferTimer > 0) {
        if (coyoteTimer > 0) {
          vy = -config.jumpImpulse;
          jumpHoldTimer = config.jumpHoldGraceSeconds;
          jumpBufferTimer = 0;
          coyoteTimer = 0;
          isGrounded = false;
          verticalState = 'jumping';
          events.push({ type: 'jump' });
        } else if (wallCling && abilities.wallJump) {
          vy = -config.wallJumpImpulseY;
          vx = wallCling === 'left' ? config.wallJumpImpulseX : -config.wallJumpImpulseX;
          facing = wallCling === 'left' ? 'right' : 'left';
          jumpHoldTimer = config.jumpHoldGraceSeconds;
          jumpBufferTimer = 0;
          wallCling = null;
          verticalState = 'jumping';
          events.push({ type: 'wall-jump', direction: facing });
        } else if (doubleJumpAvailable && abilities.doubleJump) {
          vy = -config.jumpImpulse * 0.95;
          jumpHoldTimer = config.jumpHoldGraceSeconds;
          jumpBufferTimer = 0;
          doubleJumpAvailable = false;
          verticalState = 'jumping';
          events.push({ type: 'double-jump' });
        }
      }

      // Moonlit glide (Life 9) or gravity
      const isGliding = abilities.moonlit && !isGrounded && input.jumpHeld && vy > 0;
      if (isGliding) {
        vy = Math.min(vy, config.glideFallSpeed);
        verticalState = 'gliding';
        if (s === 0) events.push({ type: 'glide', active: true });
      } else {
        if (input.jumpHeld && jumpHoldTimer > 0 && vy < 0) {
          vy += config.gravity * 0.5 * sdt;
        } else {
          jumpHoldTimer = 0;
          vy += config.gravity * sdt;
        }
        vy = Math.min(vy, wallCling ? config.wallSlideSpeed : config.maxFallSpeed);
      }

      // Horizontal movement
      const nextX = x + vx * sdt;
      if (!boxCollidesSolid(grid, nextX, y, PLAYER_HALF_WIDTH, PLAYER_HALF_HEIGHT, isPhasing, crumbleTiles)) {
        x = nextX;
      } else if (abilities.ledgeGrab && vy > 0) {
        // Corner ledge hoist (Life 1)
        const cornerProbeY = y - 0.25;
        if (!boxCollidesSolid(grid, nextX, cornerProbeY, PLAYER_HALF_WIDTH, PLAYER_HALF_HEIGHT, isPhasing, crumbleTiles)) {
          y = cornerProbeY;
          x = nextX;
        } else {
          vx = 0;
        }
      } else {
        vx = 0;
      }

      // Vertical movement
      const prevY = y;
      const nextY = y + vy * sdt;
      let collidedY = false;
      if (vy > 0) {
        if (boxCollidesSolid(grid, x, nextY, PLAYER_HALF_WIDTH, PLAYER_HALF_HEIGHT, isPhasing, crumbleTiles)) {
          const tileY = Math.floor(nextY + PLAYER_HALF_HEIGHT);
          y = tileY - PLAYER_HALF_HEIGHT - 0.001;
          collidedY = true;
        } else {
          const prevFeetY = prevY + PLAYER_HALF_HEIGHT;
          const nextFeetY = nextY + PLAYER_HALF_HEIGHT;
          const tileY = Math.floor(nextFeetY);
          if (
            tileY > Math.floor(prevFeetY) &&
            (isOneWayAt(grid, Math.floor(x - PLAYER_HALF_WIDTH), tileY) || isOneWayAt(grid, Math.floor(x + PLAYER_HALF_WIDTH), tileY))
          ) {
            y = tileY - PLAYER_HALF_HEIGHT - 0.001;
            collidedY = true;
          } else {
            y = nextY;
          }
        }
        if (collidedY) {
          vy = 0;
          if (!isGrounded && !landedThisStep) {
            events.push({ type: 'land' });
            landedThisStep = true;
          }
          isGrounded = true;
          verticalState = 'grounded';
          coyoteTimer = config.coyoteTimeSeconds;
          doubleJumpAvailable = true;

          const standingTileX = Math.floor(x);
          const standingTileY = Math.floor(y + PLAYER_HALF_HEIGHT + 0.1);
          const convDir = getConveyorDirection(grid, standingTileX, standingTileY);
          if (convDir) {
            const cx = x + (convDir === 'left' ? -1 : 1) * config.conveyorSpeed * sdt;
            if (!boxCollidesSolid(grid, cx, y, PLAYER_HALF_WIDTH, PLAYER_HALF_HEIGHT, isPhasing, crumbleTiles)) x = cx;
          }
          const tileIdx = standingTileY * grid.width + standingTileX;
          if (getTileKindAt(grid, standingTileX, standingTileY) === 'crumble' && !crumbleTiles[tileIdx]) {
            crumbleTiles = { ...crumbleTiles, [tileIdx]: { index: tileIdx, timer: 0.4, fallen: false } };
          }
        } else {
          isGrounded = false;
          if (verticalState !== 'wall-sliding' && verticalState !== 'gliding') verticalState = 'falling';
        }
      } else if (vy < 0) {
        if (boxCollidesSolid(grid, x, nextY, PLAYER_HALF_WIDTH, PLAYER_HALF_HEIGHT, isPhasing, crumbleTiles)) {
          const tileY = Math.floor(nextY - PLAYER_HALF_HEIGHT);
          y = tileY + 1 + PLAYER_HALF_HEIGHT + 0.001;
          vy = 0;
          jumpHoldTimer = 0;
        } else {
          y = nextY;
        }
        isGrounded = false;
        verticalState = 'jumping';
      }
    }

    // Dynamic entities: platforms carry, vents lift, hazards kill.
    for (const ent of dynamicEntities) {
      if (ent.type === 'moving-platform') {
        const pW = (ent.width || 2) / 2;
        const pH = (ent.height || 0.5) / 2;
        const prevPos = dynamicEntityPosition(ent, prevElapsed);
        const pos = dynamicEntityPosition(ent, elapsed);
        const isOverPlatform = Math.abs(x - pos.x) < pW + PLAYER_HALF_WIDTH * 0.8;
        const isAtPlatformTop = Math.abs(y + PLAYER_HALF_HEIGHT - (pos.y - pH)) < 0.35 && vy >= 0;
        if (isOverPlatform && isAtPlatformTop) {
          y = pos.y - pH - PLAYER_HALF_HEIGHT;
          vy = 0;
          if (!isGrounded && !landedThisStep) {
            events.push({ type: 'land' });
            landedThisStep = true;
          }
          isGrounded = true;
          verticalState = 'grounded';
          coyoteTimer = config.coyoteTimeSeconds;
          doubleJumpAvailable = true;
          const carryX = x + (pos.x - prevPos.x);
          if (!boxCollidesSolid(grid, carryX, y, PLAYER_HALF_WIDTH, PLAYER_HALF_HEIGHT, isPhasing, crumbleTiles)) x = carryX;
        }
      } else if (ent.type === 'steam-vent') {
        const ventW = (ent.width || 1.2) / 2;
        const ventH = ent.height || 6.0;
        const inColumn = Math.abs(x - ent.x) < ventW && y <= ent.y && y >= ent.y - ventH;
        if (inColumn) {
          vy = Math.min(vy, -12.0);
          verticalState = 'jumping';
          isGrounded = false;
          doubleJumpAvailable = true;
        }
      } else if (ent.type === 'moving-hazard' && !isPhasing) {
        const pos = dynamicEntityPosition(ent, elapsed);
        const hw = (ent.width || 1) / 2;
        const hh = (ent.height || 1) / 2;
        if (boxesOverlap(pos.x, pos.y, hw, hh, x, y, PLAYER_HALF_WIDTH * 0.8, PLAYER_HALF_HEIGHT * 0.8)) {
          died = 'hazard-moving';
        }
      } else if (ent.type === 'patrol-bird' && !isPhasing) {
        if (Math.hypot(ent.x - x, ent.y - y) < 0.6) died = 'hazard-bird';
      }
    }
    if (died) break;

    // Projectiles
    if (projectiles.length > 0) {
      const kept: Projectile[] = [];
      for (const p0 of projectiles) {
        const p: Projectile = { ...p0, hitIds: p0.hitIds.slice() };
        p.vy += (p.spec.gravity ?? 0) * sdt;
        if (p.returning) {
          const dx = x - p.x;
          const dy = y - p.y;
          const len = Math.hypot(dx, dy) || 1;
          if (len < 0.5) continue;
          p.vx = (dx / len) * p.spec.speed;
          p.vy = (dy / len) * p.spec.speed;
        }
        const nx = p.x + p.vx * sdt;
        const ny = p.y + p.vy * sdt;
        const dist = Math.hypot(nx - p.x, ny - p.y);
        p.travelled += dist;
        const hitsSolid = isSolidAt(grid, nx, ny, false, crumbleTiles);
        if (!p.returning && (hitsSolid || p.travelled >= p.spec.range)) {
          if (p.spec.returns) {
            p.returning = true;
          } else {
            continue;
          }
        } else {
          p.x = nx;
          p.y = ny;
        }
        let consumed = false;
        if (p.owner === 'player') {
          for (const id of Object.keys(enemies)) {
            const def = defs[id];
            const e = enemies[id]!;
            if (!def || e.state === 'dead' || p.hitIds.includes(id)) continue;
            if (boxesOverlap(p.x, p.y, p.radius, p.radius, e.x, e.y, def.halfWidth, def.halfHeight)) {
              const hit = damageEnemy(e, def, p.damage, p.x, 3, 0);
              enemies = { ...enemies, [id]: hit.enemy };
              p.hitIds.push(id);
              if (hit.landed) events.push({ type: 'hit', enemyId: id, damage: p.damage, x: p.x, y: p.y, weaponId: weapon?.id ?? 'projectile' });
              if (!p.spec.returns) consumed = true;
            }
          }
        } else if (!isPhasing && invulnTimer <= 0) {
          if (boxesOverlap(p.x, p.y, p.radius, p.radius, x, y, PLAYER_HALF_WIDTH * 0.8, PLAYER_HALF_HEIGHT * 0.8)) {
            contactDamage += p.damage;
            contactFromX = p.x;
            consumed = true;
          }
        }
        if (!consumed) kept.push(p);
      }
      projectiles = kept.slice(0, 64);
    }

    // Melee and charge hitboxes
    if (attack && weapon && attack.kind !== 'ranged') {
      const box = meleeHitbox(weapon, x, y, attack.facing);
      for (const id of Object.keys(enemies)) {
        const def = defs[id];
        const e = enemies[id]!;
        if (!def || e.state === 'dead' || attack.hitIds.includes(id)) continue;
        if (boxesOverlap(box.x, box.y, box.hw, box.hh, e.x, e.y, def.halfWidth, def.halfHeight)) {
          const damage = attack.kind === 'charge' ? weapon.damage : weapon.kind === 'charge' ? Math.max(1, Math.floor(weapon.damage / 2)) : weapon.damage;
          const hit = damageEnemy(e, def, damage, x, weapon.knockback, weapon.stunSeconds);
          enemies = { ...enemies, [id]: hit.enemy };
          attack = { ...attack, hitIds: attack.hitIds.concat(id) };
          if (hit.landed) events.push({ type: 'hit', enemyId: id, damage, x: e.x, y: e.y, weaponId: weapon.id });
        }
      }
    }

    // Lethal tile hazards
    if (boxIntersectsHazard(grid, x, y, PLAYER_HALF_WIDTH * 0.8, PLAYER_HALF_HEIGHT * 0.8, isPhasing)) {
      died = 'hazard';
    }
  }

  // Crumble tile timers (once per step)
  if (Object.keys(crumbleTiles).length > 0) {
    const nextCrumble: Record<number, CrumbleTileState> = {};
    for (const [idxStr, ct] of Object.entries(crumbleTiles)) {
      const idx = Number(idxStr);
      const nextTimer = ct.timer - dt;
      if (!ct.fallen) {
        nextCrumble[idx] = nextTimer <= 0 ? { index: idx, timer: 2.0, fallen: true } : { ...ct, timer: nextTimer };
      } else if (nextTimer > 0) {
        nextCrumble[idx] = { ...ct, timer: nextTimer };
      }
    }
    crumbleTiles = nextCrumble;
  }

  // Contact damage from enemies and enemy projectiles
  if (!died && contactDamage > 0 && invulnTimer <= 0 && !isPhasing) {
    health = Math.max(0, health - contactDamage);
    invulnTimer = config.invulnerabilitySeconds;
    hurtTimer = config.hurtStunSeconds;
    const dir = contactFromX <= x ? 1 : -1;
    vx = dir * config.hurtKnockbackX;
    vy = -config.hurtKnockbackY;
    isGrounded = false;
    verticalState = 'falling';
    attack = null;
    chargeTimer = 0;
    events.push({ type: 'hurt', damage: contactDamage, health, x, y, source: 'enemy' });
    if (health <= 0) died = 'enemy';
  }

  if (died) {
    events.push({ type: 'death', reason: died, x, y });
    return {
      state: {
        ...state,
        x,
        y,
        vx: 0,
        vy: 0,
        isDead: true,
        deathReason: died,
        deathTimer: 0,
        history,
        crumbleTiles,
        elapsed,
        enemies,
        projectiles: [],
        health,
        attack: null,
      },
      events,
    };
  }

  // Checkpoint
  const reachedCheckpoint = boxIntersectsCheckpoint(grid, x, y);
  if (reachedCheckpoint && (!checkpoint || Math.hypot(checkpoint.x - reachedCheckpoint.x, checkpoint.y - reachedCheckpoint.y) > 1.0)) {
    checkpoint = reachedCheckpoint;
    events.push({ type: 'checkpoint', x: reachedCheckpoint.x, y: reachedCheckpoint.y });
  }

  // Pickups
  for (const ent of dynamicEntities) {
    if (ent.type !== 'pickup' || !ent.pickup || pickupsCollected.includes(ent.id)) continue;
    if (Math.hypot(ent.x - x, ent.y - y) < 0.7) {
      pickupsCollected = pickupsCollected.concat(ent.id);
      if (ent.pickup.kind === 'health') health = Math.min(config.maxHealth, health + (ent.pickup.amount ?? 1));
      events.push({ type: 'pickup', pickupId: ent.id, kind: ent.pickup.kind, itemId: ent.pickup.itemId, x: ent.x, y: ent.y });
    }
  }

  // Triggers
  const triggers: TriggerVolume[] = extras.triggers ?? [];
  if (triggers.length > 0 || activeTriggers.length > 0) {
    const nowInside: string[] = [];
    for (const t of triggers) {
      const inside = Math.abs(x - t.center[0]) <= t.size[0] / 2 && Math.abs(y - t.center[1]) <= t.size[1] / 2;
      if (inside) {
        nowInside.push(t.id);
        if (!activeTriggers.includes(t.id)) events.push({ type: 'trigger-enter', triggerId: t.id, eventName: t.eventName, params: t.params });
      }
    }
    for (const id of activeTriggers) {
      if (!nowInside.includes(id)) {
        const t = triggers.find((v) => v.id === id);
        events.push({ type: 'trigger-exit', triggerId: id, eventName: t?.eventName });
      }
    }
    activeTriggers = nowInside;
  }

  // Goal
  if (!hasWon && boxIntersectsGoal(grid, x, y)) {
    hasWon = true;
    events.push({ type: 'victory', x, y });
  }

  return {
    state: {
      x,
      y,
      vx,
      vy,
      verticalState,
      facing,
      isGrounded,
      wallCling,
      coyoteTimer,
      jumpBufferTimer,
      jumpHoldTimer,
      dashTimer,
      dashCooldownTimer,
      teleportCooldownTimer,
      shadowFormTimer,
      shadowFormCooldownTimer,
      doubleJumpAvailable,
      isDead: false,
      deathReason: null,
      deathTimer: 0,
      checkpoint,
      history,
      ghostDouble,
      crumbleTiles,
      elapsed,
      health,
      invulnTimer,
      hurtTimer,
      attack,
      attackCooldownTimer,
      chargeTimer,
      enemies,
      projectiles,
      pickupsCollected,
      activeTriggers,
      hasWon,
    },
    events,
  };
}
